import React, { useRef, useState } from "react";
import { Upload, Loader2, Link2, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ImageUploadField = ({ label, value, onChange, aspect = "aspect-video" }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [showUrl, setShowUrl] = useState(false);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div className="mb-4">
            {label && <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mb-1.5">{label}</div>}
            <div className="flex gap-3 items-start">
                <div className={`relative w-28 shrink-0 ${aspect} rounded-lg overflow-hidden border border-white/15 bg-bg-base grid place-items-center`}>
                    {value ? (
                        <>
                            <img src={value} alt="preview" className="w-full h-full object-cover" />
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-white/80 hover:text-red-300"
                                aria-label="Remove image"
                            >
                                <X size={12} />
                            </button>
                        </>
                    ) : (
                        <span className="text-white/30 text-xs px-2 text-center">No image</span>
                    )}
                </div>

                <div className="flex-1 space-y-2">
                    <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`upload-input-${(label || "img").toLowerCase().replace(/\s+/g, "-")}`} />
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        data-testid={`upload-btn-${(label || "img").toLowerCase().replace(/\s+/g, "-")}`}
                        className="btn-gold text-sm inline-flex items-center gap-2 w-full justify-center"
                    >
                        {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Upload from device</>}
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowUrl((s) => !s)}
                        className="text-xs text-white/50 hover:text-gold inline-flex items-center gap-1.5"
                    >
                        <Link2 size={12} /> {showUrl ? "Hide URL" : "Or paste image URL"}
                    </button>
                    {showUrl && (
                        <input
                            type="text"
                            value={value || ""}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="https://…"
                            className="w-full bg-bg-base border border-white/15 rounded-lg px-3 py-2 text-white text-xs focus:border-gold focus:outline-none"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImageUploadField;
